import { normalizeCrmActionHref } from "./safe-action-hrefs";

export type CrmContactInteractionTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface CrmContactInteractionTimelineItem {
  key: string;
  label: string;
  occurredAt: string | null;
  channel: string | null;
  outcome: string | null;
  description: string | null;
  href: string | null;
  actionLabel: string | null;
  tone: CrmContactInteractionTone;
}

export interface BuildCrmContactInteractionTimelineOptions {
  timelineKeys?: readonly string[];
  maxItems?: number;
}

export const CRM_CONTACT_INTERACTION_TIMELINE_FIELD_KEYS = [
  "crmInteractionTimeline",
  "contactInteractionTimeline",
  "interactionTimeline",
  "crmRecentInteractions",
  "recentInteractions",
] as const;

const PRIVATE_KEY_PATTERN = /(?:password|secret|token|authorization|credential|private[_-]?key|api[_-]?key)/i;
const MAX_TEXT_LENGTH = 180;

export function buildCrmContactInteractionTimeline(
  customFields: unknown,
  options: BuildCrmContactInteractionTimelineOptions = {},
): CrmContactInteractionTimelineItem[] {
  const fields = asRecord(customFields);
  if (!fields) return [];

  const timelineKeys = options.timelineKeys?.length
    ? [...options.timelineKeys]
    : [...CRM_CONTACT_INTERACTION_TIMELINE_FIELD_KEYS];
  const maxItems = normalizePositiveInteger(options.maxItems, 12);

  return timelineKeys
    .flatMap((key) => parseTimelineEntries(key, fields[key]))
    .filter((item): item is CrmContactInteractionTimelineItem => Boolean(item))
    .sort(compareTimelineItems)
    .slice(0, maxItems);
}

function parseTimelineEntries(rootKey: string, value: unknown): Array<CrmContactInteractionTimelineItem | null> {
  if (Array.isArray(value)) {
    return value.map((entry, index) => parseTimelineItem(`${rootKey}.${index}`, entry));
  }

  const record = asRecord(value);
  if (!record) return [];

  if (hasTimelineItemShape(record)) {
    return [parseTimelineItem(rootKey, record)];
  }

  return Object.entries(record).map(([key, entry]) => parseTimelineItem(key, entry));
}

function parseTimelineItem(
  fallbackKey: string,
  value: unknown,
): CrmContactInteractionTimelineItem | null {
  if (isPrivateKey(fallbackKey)) return null;

  const record = asRecord(value);
  const key = record ? readString(record.key) ?? fallbackKey : fallbackKey;
  if (isPrivateKey(key)) return null;

  if (!record) {
    const description = formatTimelineText(value);
    if (!description) return null;
    return {
      key,
      label: formatLabel(key),
      occurredAt: null,
      channel: null,
      outcome: null,
      description,
      href: null,
      actionLabel: null,
      tone: "neutral",
    };
  }

  const label = readString(record.label)
    ?? readString(record.title)
    ?? readString(record.eventType)
    ?? readString(record.subject)
    ?? readString(record.type)
    ?? formatLabel(key);
  const occurredAt = normalizeTimestamp(
    readString(record.occurredAt)
    ?? readString(record.createdAt)
    ?? readString(record.timestamp)
    ?? readString(record.performedAt)
    ?? readString(record.at),
  );
  const channel = truncate(readString(record.channel) ?? readString(record.source) ?? readString(record.medium));
  const outcome = truncate(readString(record.outcome) ?? readString(record.status) ?? readString(record.result));
  const description = truncate(
    readString(record.description)
    ?? readString(record.summary)
    ?? readString(record.evidence)
    ?? readString(record.body),
  );
  const href = normalizeHref(readString(record.href) ?? readString(record.url) ?? readString(record.to));
  const actionLabel = truncate(
    readString(record.actionLabel)
    ?? readString(record.ctaLabel)
    ?? readString(record.linkLabel),
  );

  if (!occurredAt && !channel && !outcome && !description && !href) return null;

  return {
    key,
    label: truncate(label) ?? formatLabel(key),
    occurredAt,
    channel,
    outcome,
    description,
    href,
    actionLabel,
    tone: normalizeTone(readString(record.tone) ?? readString(record.status) ?? readString(record.outcome)),
  };
}

function hasTimelineItemShape(record: Record<string, unknown>): boolean {
  return [
    "label",
    "title",
    "eventType",
    "subject",
    "type",
    "occurredAt",
    "createdAt",
    "timestamp",
    "performedAt",
    "at",
    "channel",
    "source",
    "medium",
    "outcome",
    "status",
    "result",
    "description",
    "summary",
    "evidence",
    "body",
    "href",
    "url",
    "to",
  ].some((key) => key in record);
}

function compareTimelineItems(
  left: CrmContactInteractionTimelineItem,
  right: CrmContactInteractionTimelineItem,
): number {
  const leftTime = toTime(left.occurredAt);
  const rightTime = toTime(right.occurredAt);
  if (leftTime === null && rightTime === null) return 0;
  if (leftTime === null) return 1;
  if (rightTime === null) return -1;
  return rightTime - leftTime;
}

function normalizeTone(value: string | null): CrmContactInteractionTone {
  if (value === "success" || value === "warning" || value === "danger" || value === "info") return value;
  if (value === "critical" || value === "error" || value === "failed" || value === "failure") return "danger";
  if (value === "ready" || value === "passed" || value === "complete" || value === "completed" || value === "booked") return "success";
  if (value === "pending" || value === "at_risk" || value === "needs_review") return "warning";
  return "neutral";
}

function normalizeHref(value: string | null): string | null {
  return normalizeCrmActionHref(value);
}

function normalizeTimestamp(value: string | null): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return null;
  return new Date(timestamp).toISOString();
}

function toTime(value: string | null): number | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : null;
}

function formatTimelineText(value: unknown): string | null {
  if (typeof value === "string") return truncate(value.trim());
  if (typeof value === "number") return Number.isFinite(value) ? String(value) : null;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return null;
}

function formatLabel(value: string): string {
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function normalizePositiveInteger(value: number | undefined, fallback: number): number {
  return typeof value === "number" && Number.isInteger(value) && value > 0 ? value : fallback;
}

function truncate(value: string | null): string | null {
  if (!value) return null;
  return value.length > MAX_TEXT_LENGTH ? `${value.slice(0, MAX_TEXT_LENGTH - 3)}...` : value;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function isPrivateKey(key: string): boolean {
  return PRIVATE_KEY_PATTERN.test(key);
}
